import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import Button from "../../../../components/button/Button";
import {
  getCampaignDetail,
  createCampaign,
  updateCampaign,
  uploadCampaignFile,
  getCampaignCountries,
  getCampaignStates,
  getCampaignCategories
} from "../../../../utils/apis/services/api";
import "./campaignForm.scss";

const COUNTRY_OPTIONS = [
  { value: "jamaica", label: "Jamaica" },
  { value: "usa", label: "USA" },
  { value: "india", label: "India" },
  { value: "can", label: "Canada" }
];

const DAYS = [
  { dayName: "Mo", dayValue: "monday" },
  { dayName: "Tu", dayValue: "tuesday" },
  { dayName: "We", dayValue: "wednesday" },
  { dayName: "Th", dayValue: "thursday" },
  { dayName: "Fr", dayValue: "friday" },
  { dayName: "Sa", dayValue: "saturday" },
  { dayName: "Su", dayValue: "sunday" }
];

const CategoryBased = ({ id }) => {
  const history = useHistory();
  const [formValue, setFormValue] = useState({
    name: "",
    country: "",
    state_id: "",
    city_id: "",
    startDate: "",
    endDate: "",
    description: ""
  });
  const [selectedDays, setSelectedDays] = useState([]);
  const [uploadedImage, setUploadedImage] = useState("");
  const [uploadedImageId, setUploadedImageId] = useState("");
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [cat1List, setCat1List] = useState([]);
  const [cat2List, setCat2List] = useState([]);
  const [cat3List, setCat3List] = useState([]);
  const [cat4List, setCat4List] = useState([]);
  const [cat1, setCat1] = useState("");
  const [cat2, setCat2] = useState("");
  const [cat3, setCat3] = useState("");
  const [cat4, setCat4] = useState("");

  useEffect(() => {
    loadCountries();
    const init = async () => {
      let arr = await loadCategories();
      setCat1List(arr);
    };
    init();
    if (id !== "Create") loadCampaign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadCountries = async () => {
    let res = await getCampaignCountries();
    if (res?.data) {
      let arr = Array.isArray(res.data) ? res.data : res.data.response || res.data.data || [];
      setCountryList(arr);
    }
  };

  const loadStates = async (country) => {
    if (!country) return setStateList([]);
    let res = await getCampaignStates(country);
    if (res?.data) {
      let d = res.data.response?.[0]?.data || res.data;
      setStateList(d.states || []);
    }
  };

  const loadCities = async (country, stateId) => {
    if (!country || !stateId) return setCityList([]);
    let res = await getCampaignStates(country, stateId);
    if (res?.data) {
      let d = res.data.response?.[0]?.data || res.data;
      setCityList(d.cities || []);
    }
  };

  const loadCategories = async () => {
    let res = await getCampaignCategories();
    if (res?.data) {
      let raw = res.data;
      let arr = raw.ResponseData ?? raw.response ?? raw.results ?? raw.SubCategory ?? raw.data ?? (Array.isArray(raw) ? raw : []);
      return Array.isArray(arr) ? arr : [];
    }
    return [];
  };

  const loadCampaign = async () => {
    let res = await getCampaignDetail(id);
    if (res?.data && (res.status === 200 || res.status === 201)) {
      let d = res.data.response || res.data.ResponseData || res.data;
      setFormValue({
        name: d.name || "",
        country: d.country || "",
        state_id: d.state_id || "",
        city_id: d.city_id || "",
        startDate: d.start_date || d.startDate || "",
        endDate: d.end_date || d.endDate || "",
        description: d.description || ""
      });
      setSelectedDays(d.weekday || (d.weekdays || []).map((w) => w.slug || w));
      setUploadedImage(d.banner_image_url || d.banner_image?.file || d.bannerImage?.file || d.bannerImage?.url || "");
      setUploadedImageId(d.banner_image || d.bannerImage?.id || "");
      const cats = d.category || d.categories || [];
      if (cats[0]) setCat1(typeof cats[0] === "object" ? cats[0].slug : cats[0]);
      if (cats[1]) setCat2(typeof cats[1] === "object" ? cats[1].slug : cats[1]);
      if (cats[2]) setCat3(typeof cats[2] === "object" ? cats[2].slug : cats[2]);
      if (cats[3]) setCat4(typeof cats[3] === "object" ? cats[3].slug : cats[3]);
    }
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormValue((p) => ({ ...p, [name]: value }));
    if (name === "country") {
      loadStates(value);
      setFormValue((p) => ({ ...p, state_id: "", city_id: "" }));
      setStateList([]);
      setCityList([]);
    }
    if (name === "state_id") {
      loadCities(formValue.country, value);
      setFormValue((p) => ({ ...p, city_id: "" }));
    }
  };

  const onCountryChange = (e) => {
    handleInput(e);
    loadStates(e.target.value);
  };

  const onStateChange = (e) => {
    setFormValue((p) => ({ ...p, state_id: e.target.value, city_id: "" }));
    loadCities(formValue.country, e.target.value);
  };

  const onCat1Change = (e) => {
    const v = e.target.value;
    setCat1(v);
    setCat2("");
    setCat3("");
    setCat4("");
    setCat2List([]);
    setCat3List([]);
    setCat4List([]);
  };

  const onCat2Change = (e) => {
    setCat2(e.target.value);
    setCat3("");
    setCat4("");
    setCat3List([]);
    setCat4List([]);
  };

  const onCat3Change = (e) => {
    setCat3(e.target.value);
    setCat4("");
    setCat4List([]);
  };

  const onCat4Change = (e) => setCat4(e.target.value);

  const selectDays = (val) => {
    setSelectedDays((p) => (p.includes(val) ? p.filter((d) => d !== val) : [...p, val]));
  };

  const onDrop = useCallback(async (files) => {
    if (files.length === 0) return;
    let fd = new FormData();
    fd.append("file", files[0]);
    let res = await uploadCampaignFile(fd);
    if (!res || !res.data) {
      toast.error(res?.data?.response || "Upload failed");
      return;
    }
    let raw = res.data.response || res.data.ResponseData || res.data;
    let item = Array.isArray(raw) ? raw[0] : raw;
    if (item && (item.id != null || item.file || item.url)) {
      if (item.id != null) setUploadedImageId(item.id);
      setUploadedImage(item.file || item.url || item.banner_image_url || "");
    } else {
      toast.error("Upload failed");
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: "image/*",
    multiple: false,
    maxSize: 5 * 1024 * 1024
  });

  const validate = () => {
    if (!formValue.name) return "Name required";
    if (selectedDays.length === 0) return "Select week days";
    if (!formValue.startDate) return "Start date required";
    if (!formValue.endDate) return "End date required";
    if (!uploadedImageId) return "Banner image required";
    if (!cat1) return "Category required";
    return "";
  };

  const handleSubmit = (e) => {
    e && e.preventDefault();
    let err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    let categoryArr = [cat1, cat2, cat3, cat4].filter(Boolean);
    let body = {
      name: formValue.name,
      banner_image: uploadedImageId,
      country: formValue.country,
      start_date: formValue.startDate,
      end_date: formValue.endDate,
      description: formValue.description,
      weekday: selectedDays,
      banner_type: "category-based",
      is_active: true,
      category: categoryArr,
      product: [],
      merchant: []
    };
    const run = async () => {
      let res = id === "Create" ? await createCampaign(body) : await updateCampaign(id, body);
      if (res && (res.status === 200 || res.status === 201)) {
        toast.success(res.data?.response || "Saved");
        history.push("/campaign");
      } else {
        toast.error(res?.data?.response || "Failed");
      }
    };
    run();
  };

  const goBack = () => history.goBack();

  const getCountryVal = (c) => c?.iso_3166_1_a3 || c?.id || c;
  const getCountryLabel = (c) => c?.printable_name || c?.name || c;
  const getStateVal = (s) => s?.id || s;
  const getStateLabel = (s) => s?.stateName || s?.name || s;
  const getCityVal = (c) => c?.id || c;
  const getCityLabel = (c) => c?.cityName || c?.name || c;
  const getCatVal = (c) => (c && (c.slug ?? c.id ?? c.value ?? c)) || "";
  const getCatLabel = (c) => (c && (c.name ?? c.title ?? c.label ?? c)) || "";

  return (
    <div className="campaign-form">
      <div className="campaign-form-back" onClick={goBack}>
        <i className="fa fa-arrow-left" /> Listings
      </div>
      <form onSubmit={handleSubmit} onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}>
        <div className="campaign-form-row">
          <div className="campaign-form-group">
            <label>Campaign name *</label>
            <input name="name" value={formValue.name} onChange={handleInput} required />
          </div>
          <div className="campaign-form-group">
            <label>Country *</label>
            {/* {countryList.length > 0 ? ( */}
              <select name="country" value={formValue.country} onChange={onCountryChange} required>
                <option value="">Select</option>
                {COUNTRY_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
                {/* {countryList.map((c, i) => (
                  <option key={i} value={getCountryVal(c)}>{getCountryLabel(c)}</option>
                ))} */}
              </select>
            {/* ) : (
              <input name="country" value={formValue.country} onChange={handleInput} required />
            )} */}
          </div>
        </div>
        {/* <div className="campaign-form-row">
          <div className="campaign-form-group">
            <label>State *</label>
            {stateList.length > 0 ? (
              <select name="state_id" value={formValue.state_id} onChange={onStateChange} required>
                <option value="">Select</option>
                {stateList.map((s, i) => (
                  <option key={i} value={getStateVal(s)}>{getStateLabel(s)}</option>
                ))}
              </select>
            ) : (
              <input name="state_id" value={formValue.state_id} onChange={handleInput} required />
            )}
          </div>
          <div className="campaign-form-group">
            <label>City *</label>
            {cityList.length > 0 ? (
              <select name="city_id" value={formValue.city_id} onChange={handleInput} required>
                <option value="">Select</option>
                {cityList.map((c, i) => (
                  <option key={i} value={getCityVal(c)}>{getCityLabel(c)}</option>
                ))}
              </select>
            ) : (
              <input name="city_id" value={formValue.city_id} onChange={handleInput} required />
            )}
          </div>
        </div> */}
        <div className="campaign-form-row">
          <div className="campaign-form-group">
            <label>Category *</label>
            {cat1List.length > 0 ? (
              <select value={cat1} onChange={onCat1Change} required>
                <option value="">Select</option>
                {cat1List.map((c, i) => (
                  <option key={i} value={getCatVal(c)}>{getCatLabel(c)}</option>
                ))}
              </select>
            ) : (
              <input value={cat1} onChange={(e) => setCat1(e.target.value)} placeholder="Enter category" required />
            )}
          </div>
          {/* {cat2List.length > 0 && (
            <div className="campaign-form-group">
              <label>Sub Category</label>
              <select value={cat2} onChange={onCat2Change}>
                <option value="">Select</option>
                {cat2List.map((c, i) => (
                  <option key={i} value={getCatVal(c)}>{getCatLabel(c)}</option>
                ))}
              </select>
            </div>
          )} */}
        {/* </div>
        <div className="campaign-form-row">
          {cat3List.length > 0 && (
            <div className="campaign-form-group">
              <label>Sub Sub Category</label>
              <select value={cat3} onChange={onCat3Change}>
                <option value="">Select</option>
                {cat3List.map((c, i) => (
                  <option key={i} value={getCatVal(c)}>{getCatLabel(c)}</option>
                ))}
              </select>
            </div>
          )}
          {cat4List.length > 0 && (
            <div className="campaign-form-group">
              <label>Sub Sub Sub Category</label>
              <select value={cat4} onChange={onCat4Change}>
                <option value="">Select</option>
                {cat4List.map((c, i) => (
                  <option key={i} value={getCatVal(c)}>{getCatLabel(c)}</option>
                ))}
              </select>
            </div>
          )} */}
        {/* </div>
        <div className="campaign-form-row"> */}
          <div className="campaign-form-group">
            <label>Select days *</label>
            <div className="days-select">
              {DAYS.map((d) => (
                <span
                  key={d.dayValue}
                  className={selectedDays.includes(d.dayValue) ? "day active" : "day"}
                  onClick={() => selectDays(d.dayValue)}
                >
                  {d.dayName}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="campaign-form-row">
          <div className="campaign-form-group">
            <label>Start Date *</label>
            <input type="date" name="startDate" value={formValue.startDate} onChange={handleInput} required />
          </div>
          <div className="campaign-form-group">
            <label>End Date *</label>
            <input type="date" name="endDate" value={formValue.endDate} onChange={handleInput} required />
          </div>
        </div>
        <div className="campaign-form-row">
          <div className="campaign-form-group">
            <label>Description</label>
            <textarea name="description" value={formValue.description} onChange={handleInput} rows={4} />
          </div>
        </div>
        <div className="campaign-form-row">
          <div className="campaign-form-group">
            <label>Banner image *</label>
            <div className="banner-upload">
              {uploadedImage && (
                <div className="banner-preview">
                  <img src={uploadedImage} alt="" />
                </div>
              )}
              <div {...getRootProps()} className="banner-drop">
                <input {...getInputProps()} />
                <i className="fa fa-plus" /> Add Photo
              </div>
            </div>
          </div>
        </div>
        <div className="campaign-form-actions">
          <Button type="button" title="Cancel" onClick={goBack} />
          <button type="submit" className="btn-primary">
            {id === "Create" ? "Create" : "Update"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CategoryBased;
